function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
import * as React from "react";
import { debounce, dispatchCustomEvent, replaceSelector, useLayoutEffect } from "./utils";
const enhanceIXData = (data, styles)=>{
    const newIXData = structuredClone(data);
    for(const id in newIXData.events){
        const { target, targets } = newIXData.events[id];
        for (const t of [
            target,
            ...targets
        ]){
            if (t.appliesTo !== "CLASS") continue;
            t.selector = replaceSelector(t.selector, styles);
        }
    }
    for(const id in newIXData.actionLists){
        const { actionItemGroups, continuousParameterGroups } = newIXData.actionLists[id];
        if (actionItemGroups) {
            for (const { actionItems } of actionItemGroups){
                for (const { config } of actionItems){
                    const { selector } = config.target;
                    if (!selector) continue;
                    config.target.selector = replaceSelector(selector, styles);
                }
            }
        }
        if (continuousParameterGroups) {
            for (const group of continuousParameterGroups){
                for (const { actionItems } of group.continuousActionGroups){
                    for (const { config } of actionItems){
                        const { selector } = config.target;
                        if (!selector) continue;
                        config.target.selector = replaceSelector(selector, styles);
                    }
                }
            }
        }
    }
    return newIXData;
};
export const IXContext = /*#__PURE__*/ React.createContext({
    initEngine: null,
    restartEngine: null
});
export const InteractionsProvider = ({ children, createEngine })=>{
    const ixData = React.useRef({});
    const ixStyles = React.useRef();
    const ixEngine = React.useRef();
    const debouncedInit = React.useRef(debounce((data, styles)=>{
        if (!ixEngine.current) ixEngine.current = createEngine();
        const newData = styles ? enhanceIXData(data, styles) : data;
        ixEngine.current.init(newData);
        dispatchCustomEvent(document, "IX2_PAGE_UPDATE");
    }));
    const initEngine = React.useCallback((data, styles)=>{
        if (!ixData.current.site) {
            ixData.current.site = data.site;
        }
        ixData.current.events = _object_spread({}, ixData.current.events, data.events);
        ixData.current.actionLists = _object_spread({}, ixData.current.actionLists, data.actionLists);
        if (styles) {
            var _ixStyles_current;
            ixStyles.current = (_ixStyles_current = ixStyles.current) !== null && _ixStyles_current !== void 0 ? _ixStyles_current : {};
            for(const s in styles){
                var _ixStyles_current_s;
                if (!((_ixStyles_current_s = ixStyles.current[s]) === null || _ixStyles_current_s === void 0 ? void 0 : _ixStyles_current_s.includes(styles[s]))) {
                    const currentStyle = ixStyles.current[s];
                    ixStyles.current[s] = CSS.escape(styles[s]) + (currentStyle ? ` ${currentStyle}` : "");
                }
            }
        }
        debouncedInit.current(ixData.current, ixStyles.current);
    }, []);
    return /*#__PURE__*/ React.createElement(IXContext.Provider, {
        value: {
            initEngine,
            restartEngine: ()=>debouncedInit.current && debouncedInit.current(ixData.current, ixStyles.current)
        }
    }, children);
};
export const useInteractions = (data, styles)=>{
    const { initEngine } = React.useContext(IXContext);
    React.useEffect(()=>{
        if (initEngine) initEngine(data, styles);
    }, [
        initEngine,
        data,
        styles
    ]);
    React.useEffect(()=>{
        var _document_querySelector;
        if ((_document_querySelector = document.querySelector("html")) === null || _document_querySelector === void 0 ? void 0 : _document_querySelector.hasAttribute("data-wf-page")) return;
        const hasPageInteractions = Object.values(data.events).some((event)=>event.target.appliesTo === "PAGE");
        if (hasPageInteractions) {
            document.documentElement.setAttribute("data-wf-page", "wf-page-id");
            dispatchCustomEvent(document, "IX2_PAGE_UPDATE");
        }
    }, [
        data.events
    ]);
};
export function triggerIXEvent(element, active) {
    if (!element) return;
    dispatchCustomEvent(element, active ? "COMPONENT_ACTIVE" : "COMPONENT_INACTIVE");
}
export function useIXEvent(element, active) {
    useLayoutEffect(()=>{
        triggerIXEvent(element, active);
    }, [
        element,
        active
    ]);
}
